import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useBuInstanceLabel } from "@/hooks/use-bu-instance-label";
import { useSoapCallLogDetail } from "@/hooks/use-soap-call-log";
import { formatDateTime } from "@/lib/format";

interface SoapCallLogDetailSheetProps {
  id: string | null;
  onClose: () => void;
}

function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2 text-sm">
      <span className="w-28 shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 break-all font-medium">{value}</span>
    </div>
  );
}

/**
 * Wide right-side drawer showing one SOAP call's full detail, including the
 * raw request/response XML — fetched lazily via `useSoapCallLogDetail`, only
 * once `id` is set (mirrors `ContractDetailDrawer`'s `open`-driven-by-selection
 * pattern). Request/Response is a button-group toggle over `<pre>` blocks,
 * mirroring `EditTemplateDialog`'s HTML/Plaintext preview toggle.
 */
export function SoapCallLogDetailSheet({ id, onClose }: SoapCallLogDetailSheetProps) {
  const { label } = useBuInstanceLabel();
  const [view, setView] = useState<"request" | "response">("request");
  const { data: entry, isLoading, isError, error } = useSoapCallLogDetail(id);

  return (
    <Sheet
      open={id !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent className="w-full overflow-y-auto sm:max-w-3xl">
        <SheetHeader>
          <SheetTitle>{entry ? entry.operation : "SOAP call"}</SheetTitle>
          <SheetDescription>Full request/response detail, read-only.</SheetDescription>
        </SheetHeader>

        <div className="space-y-4 px-4 pb-4">
          {isLoading && (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          )}

          {isError && (
            <div className="py-8 text-center text-sm text-destructive">
              {error?.message ?? "Failed to load call detail"}
            </div>
          )}

          {entry && (
            <>
              <div className="space-y-2 rounded-md border border-border/50 bg-muted/20 p-3">
                <FieldRow
                  label="Status"
                  value={
                    <Badge variant={entry.status === "success" ? "success" : "destructive"}>
                      {entry.status === "success" ? "Success" : "Error"}
                    </Badge>
                  }
                />
                <FieldRow label={label} value={entry.buInstanceName ?? "—"} />
                <FieldRow label="Endpoint" value={entry.endpointUrl} />
                <FieldRow label="HTTP status" value={entry.httpStatus ?? "—"} />
                <FieldRow label="Duration" value={`${entry.durationMs}ms`} />
                <FieldRow
                  label="Initiator"
                  value={entry.initiatorEmail ?? entry.initiatorId ?? "—"}
                />
                <FieldRow label="When" value={formatDateTime(entry.createdAt)} />
                {entry.errorMessage && (
                  <FieldRow
                    label="Error"
                    value={<span className="text-destructive">{entry.errorMessage}</span>}
                  />
                )}
              </div>

              <div className="flex min-h-[400px] flex-col rounded-md border border-border/50 bg-muted/20">
                <div className="flex items-center justify-between border-b border-border/50 px-3 py-2">
                  <span className="text-xs font-medium text-muted-foreground">Raw XML</span>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant={view === "request" ? "secondary" : "ghost"}
                      onClick={() => setView("request")}
                      className="h-7 px-2 text-xs"
                    >
                      Request
                    </Button>
                    <Button
                      size="sm"
                      variant={view === "response" ? "secondary" : "ghost"}
                      onClick={() => setView("response")}
                      className="h-7 px-2 text-xs"
                    >
                      Response
                    </Button>
                  </div>
                </div>
                <div className="flex-1 overflow-hidden bg-background">
                  <pre className="h-full overflow-auto whitespace-pre-wrap break-all p-4 text-xs leading-relaxed text-foreground">
                    {view === "request"
                      ? entry.requestBody
                      : (entry.responseBody ?? "No response received (network failure).")}
                  </pre>
                </div>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
